import { getVisibleLeaguePortals, isValidLeaguePortalId } from '@/config/leaguePortals'

/** Bump when existing users should see the checklist again after an app update. */
export const PERSONALIZATION_VERSION = '3.1'

export const LANDING_HUB = 'hub'
export const LANDING_PICKUP = 'pickup'
export const LANDING_TOURNAMENTS = 'tournaments'
export const LANDING_TRAINING = 'training'
export const LANDING_MY_TEAMS = 'myTeams'

export const HUB_CARD_IDS = [
  'continuePickup',
  'newsletter',
  'store',
  'sponsors',
  'pickup',
  'tournaments',
  'leagues'
]

/** Hub sections on CitySelect (landing) that users can show or hide. */
export const HUB_CARD_DEFINITIONS = [
  {
    id: 'continuePickup',
    label: 'Return to pickup',
    description: 'Quick link to your last pickup city when available'
  },
  {
    id: 'newsletter',
    label: 'Newsletter',
    description: 'Archive and subscribe link'
  },
  {
    id: 'store',
    label: 'Online store',
    description: 'Nova Adult Hockey merchandise'
  },
  {
    id: 'sponsors',
    label: 'Sponsors',
    description: 'Community partners'
  },
  {
    id: 'pickup',
    label: 'Pickup hockey',
    description: 'City pickup cards and weekly games'
  },
  {
    id: 'tournaments',
    label: 'Tournaments',
    description: 'Invitational and Team Events'
  },
  {
    id: 'leagues',
    label: 'Leagues',
    description: 'Public league schedules and standings'
  }
]

function defaultVisibleCards () {
  return Object.fromEntries(HUB_CARD_IDS.map((id) => [id, true]))
}

export const DEFAULT_HUB_PERSONALIZATION = {
  visibleCards: defaultVisibleCards(),
  defaultLanding: LANDING_HUB,
  completedAt: null,
  promptVersion: null
}

export function mergeHubPersonalization (raw) {
  const source = raw && typeof raw === 'object' ? raw : {}
  const visibleCards = {
    ...defaultVisibleCards(),
    ...(source.visibleCards && typeof source.visibleCards === 'object' ? source.visibleCards : {})
  }

  return {
    visibleCards,
    defaultLanding: source.defaultLanding || LANDING_HUB,
    completedAt: source.completedAt || null,
    promptVersion: source.promptVersion || null
  }
}

export function needsPersonalizationPrompt (raw) {
  const merged = mergeHubPersonalization(raw)
  if (!merged.completedAt) return true
  return merged.promptVersion !== PERSONALIZATION_VERSION
}

export function buildLandingOptions ({ hasLeagueAccess = false } = {}) {
  const options = [
    {
      id: LANDING_HUB,
      label: 'All Cities & Leagues',
      description: 'Main hub with your chosen cards'
    },
    {
      id: LANDING_PICKUP,
      label: 'Pickup city home',
      description: 'Your default pickup city schedule'
    },
    {
      id: LANDING_TOURNAMENTS,
      label: 'Tournaments',
      description: 'Tournament programs and registration'
    },
    {
      id: LANDING_TRAINING,
      label: 'Training',
      description: 'Training sessions hub'
    }
  ]

  if (hasLeagueAccess) {
    options.push({
      id: LANDING_MY_TEAMS,
      label: 'My teams',
      description: 'League captain and team dashboard'
    })
  }

  for (const portal of getVisibleLeaguePortals()) {
    options.push({
      id: `league:${portal.id}`,
      label: portal.displayName,
      description: 'Public league schedule and standings'
    })
  }

  return options
}

/**
 * @returns {string|null} Path to redirect to, or null to stay on hub.
 */
export function resolveDefaultLandingRoute (profile, { hasLeagueAccess = false } = {}) {
  const { defaultLanding } = mergeHubPersonalization(profile?.hubPersonalization)

  if (!defaultLanding || defaultLanding === LANDING_HUB) {
    return null
  }

  if (defaultLanding === LANDING_PICKUP) {
    const cityId = String(profile?.defaultCityId || 'halifax').trim() || 'halifax'
    return `/${cityId}`
  }

  if (defaultLanding === LANDING_TOURNAMENTS) {
    return '/tournaments'
  }

  if (defaultLanding === LANDING_TRAINING) {
    return '/training'
  }

  if (defaultLanding === LANDING_MY_TEAMS) {
    return hasLeagueAccess ? '/league' : null
  }

  if (defaultLanding.startsWith('league:')) {
    const leagueId = defaultLanding.slice('league:'.length)
    if (isValidLeaguePortalId(leagueId)) {
      return `/leagues/${leagueId}/home`
    }
  }

  return null
}
